import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Drawer, message, theme, Select, Card } from 'antd';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import CSVImporter from '../../../../common/CSVImporter';
import { useVisaApplicationCreate } from '../hooks/use-visa-application.hook';
import { useStatusList } from '../../statuses/hooks/use-status.hook';
import {
  useTemplateMappingList,
  useTemplateMappingCreate,
  useTemplateMappingDelete,
} from '../hooks/use-template-mapping.hook';
import { extractValidationError } from '../../../../../services/extract-api-error.service';
import { ValidationError, ApiResponseWithSummary } from '../../../../../types/api-response.types';
import { VisaApplication } from '../types/visa-application.types';
import ValidationErrorsPreview from '../../../../common/ValidationErrorsPreview';
import { FailedRecord, Summary } from '../../../../common/BulkImportSummaryPreview';
import { useActiveTenant } from '../../../../../states/active-tenant.atom';
import FormBuilder, { FormField } from '../../../../common/FormBuilder';
import SaveTemplateModal from '../components/save-template-modal.component';

interface VisaApplicationBulkImportContainerProps {
  visible: boolean;
  onClose: () => void;
}

const VisaApplicationBulkImportContainer: React.FC<VisaApplicationBulkImportContainerProps> = ({
  visible,
  onClose,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { activeTenant } = useActiveTenant();
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [failedRecords, setFailedRecords] = useState<FailedRecord<VisaApplication>[]>([]);
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary] = useState<Summary>({
    successful: 0,
    failed: 0,
    total: 0,
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [currentMappings, setCurrentMappings] = useState<Record<string, string>>({});
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [pendingImportData, setPendingImportData] = useState<any[] | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<{ visaStatus: string }>({
    defaultValues: {
      visaStatus: '',
    },
  });

  // Fetch statuses for dropdown
  const { data: statusesData } = useStatusList({ page: 1, limit: 100 });
  const statuses = useMemo(() => statusesData?.docs || [], [statusesData?.docs]);
  
  const statusOptions = useMemo(() => {
    return statuses.map((status) => ({
      label: status.statusCode,
      value: status.id,
    }));
  }, [statuses]);

  // Watch visaStatus from form
  const selectedVisaStatus = watch('visaStatus');

  const handleCloseDrawer = useCallback(() => {
    onClose();
  }, [onClose]);

  const { mutateAsync: createVisaApplication, isPending } = useVisaApplicationCreate();
  const { mutateAsync: createTemplate, isPending: isCreatingTemplate } = useTemplateMappingCreate();
  const { mutateAsync: deleteTemplate } = useTemplateMappingDelete();

  // Load templates when CSV headers are available
  const {
    data: templatesData,
    isLoading: templatesLoading,
    refetch: refetchTemplates,
  } = useTemplateMappingList({
    page: 1,
    limit: 100, // Load all templates
  });

  const templates = useMemo(() => {
    if (!templatesData?.data?.docs) return [];
    return templatesData.data.docs.map((template) => ({
      id: template.id,
      name: template.name,
      mappingTemplate: template.mappingTemplate,
    }));
  }, [templatesData]);

  // Helper function to transform flat CSV data into nested structure
  const transformFlatDataToNested = useCallback(
    (flatRecord: any, selectedStatusId?: string) => {
      const nestedRecord: any = {
        tenantId: activeTenant?.id,
      };

      // Top-level fields
      const topLevelFields = [
        'mrz',
        'passportNumber',
        'applicationNumber',
        'visaType',
        'visaUidNumber',
        'visaFileNumber',
        'visaIssueDate',
        'visaExpiryDate',
        'visaName',
        'visaSponsor',
      ];

      topLevelFields.forEach((field) => {
        if (flatRecord[field] !== undefined && flatRecord[field] !== '') {
          nestedRecord[field] = flatRecord[field];
        }
      });

      // Always use the selected visa status from dropdown
      if (selectedStatusId) {
        nestedRecord.visaStatus = selectedStatusId;
      }

      // Client Data
      const clientDataFields = [
        'clientName',
        'orgLevel1',
        'orgLevel2',
        'orgLevel3',
        'orgLevel4',
        'orgLevel5',
        'orgLevel6',
        'orgLevel7',
        'tradeLicenseNumber',
        'tradeLicenseIssueDate',
        'tradeLicenseExpiryDate',
        'estCardNumber',
        'estCardIssueDate',
        'estCardExpiryDate',
        'companyAddress',
      ];
      const clientData: any = {};
      let hasClientData = false;
      clientDataFields.forEach((field) => {
        if (
          flatRecord[`clientData.${field}`] !== undefined &&
          flatRecord[`clientData.${field}`] !== ''
        ) {
          clientData[field] = flatRecord[`clientData.${field}`];
          hasClientData = true;
        }
      });
      if (hasClientData) nestedRecord.clientData = clientData;

      // Employee Data
      const employeeDataFields = [
        'candidateId',
        'employeeId',
        'probotId',
        'employmentStartDateEstimate',
        'employmentStartDateActual',
        'employmentEndDate',
        'addressHome',
        'addressLocal',
        'phoneHome',
        'educationLevel',
        'educationDescription',
        'maritalStatus',
        'firstLanguage',
        'numberOfChildren',
        'motherName',
        'fatherName',
        'typedFirstName',
        'typedMiddleName',
        'typedLastName',
        'typedDateOfBirth',
        'typedGender',
        'typedNationality',
        'biodataVerified',
        'companyDesignation',
        'mappedDesignation',
        'workType',
        'previousNationality',
        'askingForAddressInUae',
        'askingForMobileNumber',
      ];
      const employeeData: any = {};
      let hasEmployeeData = false;
      employeeDataFields.forEach((field) => {
        if (
          flatRecord[`employeeData.${field}`] !== undefined &&
          flatRecord[`employeeData.${field}`] !== ''
        ) {
          employeeData[field] = flatRecord[`employeeData.${field}`];
          hasEmployeeData = true;
        }
      });
      if (hasEmployeeData) nestedRecord.employeeData = employeeData;

      // Passport Data
      const passportDataFields = [
        'passportFirstName',
        'passportMiddleName',
        'passportLastName',
        'passportIssueDate',
        'passportExpiryDate',
        'passportDateOfBirth',
        'passportGender',
        'passportNationality',
        'passportIssuingAuthority',
        'passportIssuingCountry',
        'passportPlaceOfBirth',
      ];
      const passportData: any = {};
      let hasPassportData = false;
      passportDataFields.forEach((field) => {
        if (
          flatRecord[`passportData.${field}`] !== undefined &&
          flatRecord[`passportData.${field}`] !== ''
        ) {
          passportData[field] = flatRecord[`passportData.${field}`];
          hasPassportData = true;
        }
      });
      if (hasPassportData) nestedRecord.passportData = passportData;

      // EID Data
      const eidDataFields = [
        'eidFirstName',
        'eidMiddleName',
        'eidLastName',
        'eidNumber',
        'eidIssueDate',
        'eidExpiryDate',
        'eidDateOfBirth',
        'eidGender',
        'eidNationality',
        'eidPlaceIssued',
        'eidOccupation',
        'eidCardNumber',
      ];
      const eidData: any = {};
      let hasEidData = false;
      eidDataFields.forEach((field) => {
        if (flatRecord[`eidData.${field}`] !== undefined && flatRecord[`eidData.${field}`] !== '') {
          eidData[field] = flatRecord[`eidData.${field}`];
          hasEidData = true;
        }
      });
      if (hasEidData) nestedRecord.eidData = eidData;

      // Visa Data
      const visaDataFields = ['visaEmirate', 'visaDesignation'];
      const visaData: any = {};
      let hasVisaData = false;
      visaDataFields.forEach((field) => {
        if (
          flatRecord[`visaData.${field}`] !== undefined &&
          flatRecord[`visaData.${field}`] !== ''
        ) {
          visaData[field] = flatRecord[`visaData.${field}`];
          hasVisaData = true;
        }
      });
      if (hasVisaData) nestedRecord.visaData = visaData;

      // Drivers License Data
      const driversLicenseFields = [
        'dlFullName',
        'dlCategory1',
        'dlCategory2',
        'dlNumber',
        'dlIssueDate',
        'dlExpiryDate',
        'dlDateOfBirth',
        'dlPlaceIssued',
      ];
      const driversLicense: any = {};
      let hasDriversLicense = false;
      driversLicenseFields.forEach((field) => {
        if (
          flatRecord[`driversLicense.${field}`] !== undefined &&
          flatRecord[`driversLicense.${field}`] !== ''
        ) {
          driversLicense[field] = flatRecord[`driversLicense.${field}`];
          hasDriversLicense = true;
        }
      });
      if (hasDriversLicense) nestedRecord.driversLicense = driversLicense;

      // Recruiter Info
      const recruiterInfoFields = ['recruiterName', 'recruiterEmail', 'recruiterTelephone'];
      const recruiterInfo: any = {};
      let hasRecruiterInfo = false;
      recruiterInfoFields.forEach((field) => {
        if (
          flatRecord[`recruiterInfo.${field}`] !== undefined &&
          flatRecord[`recruiterInfo.${field}`] !== ''
        ) {
          recruiterInfo[field] = flatRecord[`recruiterInfo.${field}`];
          hasRecruiterInfo = true;
        }
      });
      if (hasRecruiterInfo) nestedRecord.recruiterInfo = recruiterInfo;

      return nestedRecord;
    },
    [activeTenant, selectedVisaStatus]
  );

  const performImport = useCallback(
    async (csvData: any) => {
      if (!activeTenant) {
        message.error('Please select a tenant');
        return;
      }

      if (!selectedVisaStatus) {
        message.error(t('visaApplications.bulkImport.visaStatusRequired') || 'Please select a visa status');
        return;
      }

      try {
        // Transform flat CSV data to nested structure
        const enrichedData = csvData.map((record: any) => transformFlatDataToNested(record, selectedVisaStatus));

        const response = await createVisaApplication(enrichedData);
        setSummary((response.data as unknown as ApiResponseWithSummary<VisaApplication>).summary);
        setFailedRecords(
          (response as unknown as ApiResponseWithSummary<VisaApplication>).data.failed
        );
        setValidationErrors([]);
        setShowSummary(true);
        message.success(t('crud.create.success', { entity: t('visaApplications.title') }));
      } catch (error) {
        const errorFields = extractValidationError(error);
        setValidationErrors(errorFields);
        message.error(t('crud.create.error', { entity: t('visaApplications.title') }));
        throw error; // Re-throw to handle in modal
      }
    },
    [createVisaApplication, t, activeTenant, transformFlatDataToNested, selectedVisaStatus]
  );

  const handleBulkCreateVisaApplication = useCallback(
    async (csvData: any) => {
      // If no template was selected, show save template modal
      if (!selectedTemplateId && Object.keys(currentMappings).length > 0) {
        setPendingImportData(csvData);
        setShowSaveTemplateModal(true);
        return;
      }

      // Otherwise, proceed with import directly
      await performImport(csvData);
    },
    [selectedTemplateId, currentMappings, performImport]
  );

  const handleSaveTemplate = useCallback(
    async (templateName: string) => {
      if (!activeTenant) {
        message.error('Please select a tenant');
        return;
      }

      try {
        await createTemplate({
          name: templateName,
          mappingTemplate: currentMappings,
        });
        message.success(
          t('visaApplications.bulkImport.templateSaved') || 'Template saved successfully'
        );
        await refetchTemplates();

        // Proceed with import after saving template
        if (pendingImportData) {
          await performImport(pendingImportData);
          setPendingImportData(null);
          setShowSaveTemplateModal(false);
        }
      } catch (error) {
        message.error(
          t('visaApplications.bulkImport.templateSaveError') || 'Failed to save template'
        );
        throw error;
      }
    },
    [
      activeTenant,
      createTemplate,
      currentMappings,
      pendingImportData,
      performImport,
      refetchTemplates,
      t,
    ]
  );

  const handleImportWithoutSaving = useCallback(async () => {
    if (pendingImportData) {
      await performImport(pendingImportData);
      setPendingImportData(null);
      setShowSaveTemplateModal(false);
    }
  }, [pendingImportData, performImport]);

  const handleTemplateDelete = useCallback(
    async (templateId: string) => {
      try {
        await deleteTemplate(templateId);
        message.success(
          t('visaApplications.bulkImport.templateDeleted') || 'Template deleted successfully'
        );
        await refetchTemplates();
      } catch (error) {
        message.error(
          t('visaApplications.bulkImport.templateDeleteError') || 'Failed to delete template'
        );
        throw error;
      }
    },
    [deleteTemplate, refetchTemplates, t]
  );

  // Reset state when drawer closes
  useEffect(() => {
    if (!visible) {
      setSelectedTemplateId(null);
      setCsvHeaders([]);
      setCurrentMappings({});
      setShowSaveTemplateModal(false);
      setPendingImportData(null);
      setShowSummary(false);
      setValidationErrors([]);
    }
  }, [visible]);

  const formFields: FormField<Record<string, never>>[] = [
    // Form fields can be added here in the future if needed
  ];

  return (
    <Drawer
      title={t('crud.create.bulkImport')}
      placement="right"
      onClose={handleCloseDrawer}
      open={visible}
      width={600}
    >
      <FormBuilder
        showSubmitButton={false}
        formFields={formFields}
        control={control as any}
        errors={errors as any}
        onSubmit={() => {}} // We don't need a submit handler as we're using the form values in CSV import
        submitButtonText={t('common.save')}
        isLoading={isPending}
        columns={2}
      />

      <CSVImporter
        templates={templates}
        templatesLoading={templatesLoading}
        selectedTemplateId={selectedTemplateId || undefined}
        onTemplateSelect={setSelectedTemplateId}
        onTemplateDelete={handleTemplateDelete}
        onMappingsChange={setCurrentMappings}
        onCsvHeadersChange={setCsvHeaders}
        additionalFields={
          <Card className="shadow-lg">
            <div className="row">
              <div className="col-12 mb-3">
                <label className="mb-2 d-block">
                  {t('visaApplications.visaStatus.label')} <span className="text-danger">*</span>
                </label>
                <Controller
                  name="visaStatus"
                  control={control}
                  rules={{
                    required: t('visaApplications.visaStatus.required') || 'Visa status is required',
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      className="w-100"
                      placeholder={t('visaApplications.visaStatus.placeholder')}
                      options={statusOptions}
                      status={errors.visaStatus ? 'error' : ''}
                    />
                  )}
                />
                {errors.visaStatus && (
                  <div className="text-danger mt-1">{errors.visaStatus.message}</div>
                )}
              </div>
            </div>
          </Card>
        }
        mappingStructure={{
          // Required fields (must be at the start)
          mrz: 'MRZ',
          passportNumber: 'Passport Number',
          applicationNumber: 'Application Number',
          visaType: 'Visa Type',
          'employeeData.typedFirstName': 'Typed First Name',
          'employeeData.typedMiddleName': 'Typed Middle Name',
          'employeeData.typedLastName': 'Typed Last Name',
          visaUidNumber: 'Visa UID Number',
          // Optional fields
          visaFileNumber: 'Visa File Number',
          visaIssueDate: 'Visa Issue Date',
          visaExpiryDate: 'Visa Expiry Date',
          visaName: 'Visa Name',
          visaSponsor: 'Visa Sponsor',
          // Client Data
          'clientData.clientName': 'Client Name',
          'clientData.orgLevel1': 'Org Level 1',
          'clientData.orgLevel2': 'Org Level 2',
          'clientData.orgLevel3': 'Org Level 3',
          'clientData.orgLevel4': 'Org Level 4',
          'clientData.orgLevel5': 'Org Level 5',
          'clientData.orgLevel6': 'Org Level 6',
          'clientData.orgLevel7': 'Org Level 7',
          'clientData.tradeLicenseNumber': 'Trade License Number',
          'clientData.tradeLicenseIssueDate': 'Trade License Issue Date',
          'clientData.tradeLicenseExpiryDate': 'Trade License Expiry Date',
          'clientData.estCardNumber': 'EST Card Number',
          'clientData.estCardIssueDate': 'EST Card Issue Date',
          'clientData.estCardExpiryDate': 'EST Card Expiry Date',
          'clientData.companyAddress': 'Company Address',
          // Employee Data
          'employeeData.candidateId': 'Candidate ID',
          'employeeData.employeeId': 'Employee ID',
          'employeeData.probotId': 'Probot ID',
          'employeeData.employmentStartDateEstimate': 'Employment Start Date Estimate',
          'employeeData.employmentStartDateActual': 'Employment Start Date Actual',
          'employeeData.employmentEndDate': 'Employment End Date',
          'employeeData.addressHome': 'Home Address',
          'employeeData.addressLocal': 'Local Address',
          'employeeData.phoneHome': 'Home Phone',
          'employeeData.educationLevel': 'Education Level',
          'employeeData.educationDescription': 'Education Description',
          'employeeData.maritalStatus': 'Marital Status',
          'employeeData.firstLanguage': 'First Language',
          'employeeData.numberOfChildren': 'Number of Children',
          'employeeData.motherName': 'Mother Name',
          'employeeData.fatherName': 'Father Name',
          'employeeData.typedDateOfBirth': 'Typed Date of Birth',
          'employeeData.typedGender': 'Typed Gender',
          'employeeData.typedNationality': 'Typed Nationality',
          'employeeData.biodataVerified': 'Biodata Verified',
          'employeeData.companyDesignation': 'Company Designation',
          'employeeData.mappedDesignation': 'Mapped Designation',
          'employeeData.workType': 'Work Type',
          'employeeData.previousNationality': 'Previous Nationality',
          'employeeData.askingForAddressInUae': 'Asking For Address in UAE',
          'employeeData.askingForMobileNumber': 'Asking For Mobile Number',
          // Passport Data
          'passportData.passportFirstName': 'Passport First Name',
          'passportData.passportMiddleName': 'Passport Middle Name',
          'passportData.passportLastName': 'Passport Last Name',
          'passportData.passportIssueDate': 'Passport Issue Date',
          'passportData.passportExpiryDate': 'Passport Expiry Date',
          'passportData.passportDateOfBirth': 'Passport Date of Birth',
          'passportData.passportGender': 'Passport Gender',
          'passportData.passportNationality': 'Passport Nationality',
          'passportData.passportIssuingAuthority': 'Passport Issuing Authority',
          'passportData.passportIssuingCountry': 'Passport Issuing Country',
          'passportData.passportPlaceOfBirth': 'Passport Place of Birth',
          // EID Data
          'eidData.eidFirstName': 'EID First Name',
          'eidData.eidMiddleName': 'EID Middle Name',
          'eidData.eidLastName': 'EID Last Name',
          'eidData.eidNumber': 'EID Number',
          'eidData.eidIssueDate': 'EID Issue Date',
          'eidData.eidExpiryDate': 'EID Expiry Date',
          'eidData.eidDateOfBirth': 'EID Date of Birth',
          'eidData.eidGender': 'EID Gender',
          'eidData.eidNationality': 'EID Nationality',
          'eidData.eidPlaceIssued': 'EID Place Issued',
          'eidData.eidOccupation': 'EID Occupation',
          'eidData.eidCardNumber': 'EID Card Number',
          // Visa Data
          'visaData.visaEmirate': 'Visa Emirate',
          'visaData.visaDesignation': 'Visa Designation',
          // Drivers License Data
          'driversLicense.dlFullName': 'Drivers License Full Name',
          'driversLicense.dlCategory1': 'Drivers License Category 1',
          'driversLicense.dlCategory2': 'Drivers License Category 2',
          'driversLicense.dlNumber': 'Drivers License Number',
          'driversLicense.dlIssueDate': 'Drivers License Issue Date',
          'driversLicense.dlExpiryDate': 'Drivers License Expiry Date',
          'driversLicense.dlDateOfBirth': 'Drivers License Date of Birth',
          'driversLicense.dlPlaceIssued': 'Drivers License Place Issued',
          // Recruiter Info
          'recruiterInfo.recruiterName': 'Recruiter Name',
          'recruiterInfo.recruiterEmail': 'Recruiter Email',
          'recruiterInfo.recruiterTelephone': 'Recruiter Telephone',
        }}
        uploadConfig={{
          allowedFileTypes: ['text/csv', 'application/vnd.ms-excel'],
          maxFileSize: 50 * 1024 * 1024, // 50MB
          multiple: false,
        }}
        onComplete={handleBulkCreateVisaApplication}
        isLoading={isPending}
        summary={summary}
        showSummary={showSummary}
        failedRecords={failedRecords}
        optionalFields={Object.keys({
          // Optional fields (all fields except required ones)
          visaFileNumber: 'Visa File Number',
          visaIssueDate: 'Visa Issue Date',
          visaExpiryDate: 'Visa Expiry Date',
          visaName: 'Visa Name',
          visaSponsor: 'Visa Sponsor',
          // Client Data
          'clientData.clientName': 'Client Name',
          'clientData.orgLevel1': 'Org Level 1',
          'clientData.orgLevel2': 'Org Level 2',
          'clientData.orgLevel3': 'Org Level 3',
          'clientData.orgLevel4': 'Org Level 4',
          'clientData.orgLevel5': 'Org Level 5',
          'clientData.orgLevel6': 'Org Level 6',
          'clientData.orgLevel7': 'Org Level 7',
          'clientData.tradeLicenseNumber': 'Trade License Number',
          'clientData.tradeLicenseIssueDate': 'Trade License Issue Date',
          'clientData.tradeLicenseExpiryDate': 'Trade License Expiry Date',
          'clientData.estCardNumber': 'EST Card Number',
          'clientData.estCardIssueDate': 'EST Card Issue Date',
          'clientData.estCardExpiryDate': 'EST Card Expiry Date',
          'clientData.companyAddress': 'Company Address',
          // Employee Data
          'employeeData.candidateId': 'Candidate ID',
          'employeeData.employeeId': 'Employee ID',
          'employeeData.probotId': 'Probot ID',
          'employeeData.employmentStartDateEstimate': 'Employment Start Date Estimate',
          'employeeData.employmentStartDateActual': 'Employment Start Date Actual',
          'employeeData.employmentEndDate': 'Employment End Date',
          'employeeData.addressHome': 'Home Address',
          'employeeData.addressLocal': 'Local Address',
          'employeeData.phoneHome': 'Home Phone',
          'employeeData.educationLevel': 'Education Level',
          'employeeData.educationDescription': 'Education Description',
          'employeeData.maritalStatus': 'Marital Status',
          'employeeData.firstLanguage': 'First Language',
          'employeeData.numberOfChildren': 'Number of Children',
          'employeeData.motherName': 'Mother Name',
          'employeeData.fatherName': 'Father Name',
          'employeeData.typedDateOfBirth': 'Typed Date of Birth',
          'employeeData.typedGender': 'Typed Gender',
          'employeeData.typedNationality': 'Typed Nationality',
          'employeeData.biodataVerified': 'Biodata Verified',
          'employeeData.companyDesignation': 'Company Designation',
          'employeeData.mappedDesignation': 'Mapped Designation',
          'employeeData.workType': 'Work Type',
          'employeeData.previousNationality': 'Previous Nationality',
          'employeeData.askingForAddressInUae': 'Asking For Address in UAE',
          'employeeData.askingForMobileNumber': 'Asking For Mobile Number',
          // Passport Data
          'passportData.passportFirstName': 'Passport First Name',
          'passportData.passportMiddleName': 'Passport Middle Name',
          'passportData.passportLastName': 'Passport Last Name',
          'passportData.passportIssueDate': 'Passport Issue Date',
          'passportData.passportExpiryDate': 'Passport Expiry Date',
          'passportData.passportDateOfBirth': 'Passport Date of Birth',
          'passportData.passportGender': 'Passport Gender',
          'passportData.passportNationality': 'Passport Nationality',
          'passportData.passportIssuingAuthority': 'Passport Issuing Authority',
          'passportData.passportIssuingCountry': 'Passport Issuing Country',
          'passportData.passportPlaceOfBirth': 'Passport Place of Birth',
          // EID Data
          'eidData.eidFirstName': 'EID First Name',
          'eidData.eidMiddleName': 'EID Middle Name',
          'eidData.eidLastName': 'EID Last Name',
          'eidData.eidNumber': 'EID Number',
          'eidData.eidIssueDate': 'EID Issue Date',
          'eidData.eidExpiryDate': 'EID Expiry Date',
          'eidData.eidDateOfBirth': 'EID Date of Birth',
          'eidData.eidGender': 'EID Gender',
          'eidData.eidNationality': 'EID Nationality',
          'eidData.eidPlaceIssued': 'EID Place Issued',
          'eidData.eidOccupation': 'EID Occupation',
          'eidData.eidCardNumber': 'EID Card Number',
          // Visa Data
          'visaData.visaEmirate': 'Visa Emirate',
          'visaData.visaDesignation': 'Visa Designation',
          // Drivers License Data
          'driversLicense.dlFullName': 'Drivers License Full Name',
          'driversLicense.dlCategory1': 'Drivers License Category 1',
          'driversLicense.dlCategory2': 'Drivers License Category 2',
          'driversLicense.dlNumber': 'Drivers License Number',
          'driversLicense.dlIssueDate': 'Drivers License Issue Date',
          'driversLicense.dlExpiryDate': 'Drivers License Expiry Date',
          'driversLicense.dlDateOfBirth': 'Drivers License Date of Birth',
          'driversLicense.dlPlaceIssued': 'Drivers License Place Issued',
          // Recruiter Info
          'recruiterInfo.recruiterName': 'Recruiter Name',
          'recruiterInfo.recruiterEmail': 'Recruiter Email',
          'recruiterInfo.recruiterTelephone': 'Recruiter Telephone',
        })}
      />

      <SaveTemplateModal
        visible={showSaveTemplateModal}
        onSave={handleSaveTemplate}
        onCancel={() => {
          setShowSaveTemplateModal(false);
          setPendingImportData(null);
        }}
        onImportWithoutSaving={handleImportWithoutSaving}
        isLoading={isPending || isCreatingTemplate}
      />

      {validationErrors.length > 0 && (
        <div className="row mt-3">
          <ValidationErrorsPreview
            validationErrors={validationErrors}
            entity={t('visaApplications.title')}
          />
        </div>
      )}
    </Drawer>
  );
};

export default VisaApplicationBulkImportContainer;
